"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseReferenceFieldLogic = void 0;
const differenceBy_1 = __importDefault(require("lodash-es/differenceBy"));
const google_sheet_1 = require("../../utils/google-sheet");
const record_1 = require("../../utils/record");
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const array_1 = require("../../helpers/array");
const validation_1 = require("../../validation");
const fields_1 = require("../../utils/fields");
const bulk_update_1 = require("../../utils/bulk-update");
class BaseReferenceFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.isReference = true;
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is_empty: comparisons_1.isEmptyList,
            is_not_empty: comparisons_1.isNotEmptyList,
            contains: comparisons_1.contains,
            not_contains: comparisons_1.notContains,
            has_all_of: comparisons_1.hasAllOf,
            has_any_of: comparisons_1.hasAnyOf,
            is_exactly: comparisons_1.isExactly,
            has_none_of: comparisons_1.hasNoneOf,
        };
    }
    get validators() {
        return { required: validation_1.requiredRefField };
    }
    valueToString(fieldValue) {
        if (!Array.isArray(fieldValue)) {
            return "";
        }
        return this.appRecordsService
            .getMultipleRelatedRecords((0, array_1.shallowUnique)(fieldValue))
            .filter(record_1.isRecordNotDeleted)
            .map((item) => (0, record_1.getTitleOrUntitledRecordString)(item))
            .join(", ");
    }
    valueToGoogleSheet(fieldValue) {
        return (0, google_sheet_1.makeRowDataValues)(this.valueToString(fieldValue), "stringValue");
    }
    getGroupValues(fieldValue) {
        const uniqueIds = (0, array_1.shallowUnique)(fieldValue);
        const referenceInstances = this.appRecordsService
            .getMultipleRelatedRecords(uniqueIds)
            .map((linkedRecord) => [linkedRecord.id]);
        return !referenceInstances.length ? [[]] : referenceInstances;
    }
    /**
     * Returns a concatenated string of labels + IDs.
     * Sort by label + ID to sort records so that the records are grouped correctly if the record title match.
     *
     * @param fieldValue    value from a record
     */
    sortKey(fieldValue) {
        if (!Array.isArray(fieldValue) || !fieldValue.length) {
            return [];
        }
        return fieldValue.map((id) => {
            const relatedRecord = this.appRecordsService.getRelatedRecord(id);
            const title = (0, record_1.getTitleOrUntitledRecordString)(relatedRecord);
            return title.toLowerCase();
        });
    }
    groupBySortKey(fieldValue) {
        if (!Array.isArray(fieldValue) || !fieldValue.length) {
            return [];
        }
        const sortKeys = this.sortKey(fieldValue);
        for (let i = 0; i < fieldValue.length; i++) {
            sortKeys.push(fieldValue[i]);
        }
        return sortKeys;
    }
    /**
     * Returns a concatenated string of IDs.
     * Group by ID to avoid having different entries in the same group if the title is equal.
     *
     * @param fieldValue    value from a record
     */
    groupByKey(fieldValue) {
        if (!Array.isArray(fieldValue) || !fieldValue.length) {
            return "";
        }
        return this.appRecordsService
            .getMultipleRelatedRecords(fieldValue)
            .map((item) => item.id)
            .join(", ");
    }
    processFilterValue(filterValue, comparison) {
        if (["is_empty", "is_not_empty"].includes(comparison)) {
            return null;
        }
        if (["contains", "not_contains"].includes(comparison)) {
            return filterValue;
        }
        return filterValue;
    }
    processFieldValue(record, slug, comparison, field) {
        const fieldValue = (0, fields_1.getValueFromRecordByField)(record, field);
        const referenceInstances = this.appRecordsService
            .getMultipleRelatedRecords(fieldValue)
            .filter(record_1.isRecordNotDeleted);
        const referenceIDs = referenceInstances.map(({ id }) => id);
        if (["is_empty", "is_not_empty"].includes(comparison)) {
            return referenceIDs;
        }
        if (["contains", "not_contains"].includes(comparison)) {
            return this.valueToString(referenceIDs);
        }
        if (!referenceIDs) {
            return [];
        }
        return referenceIDs;
    }
    getEmptyValue() {
        return [];
    }
    prepareValueForClipboard(value) {
        return {
            text: this.valueToString(value),
            rawValue: this.appRecordsService
                .getMultipleRelatedRecords(value)
                .filter(record_1.isRecordNotDeleted),
        };
    }
    canHaveReferencedRecords() {
        return true;
    }
    getReferencedRecords(fieldValue, field) {
        if (!Array.isArray(fieldValue)) {
            return [];
        }
        const applicationId = field.params.linked_application;
        return (0, array_1.removeDuplicates)(fieldValue).map((id) => ({
            recordId: id,
            application_id: applicationId,
        }));
    }
    formatActivityRecordChangeValues(previousValue, currentValue, extraData) {
        if (!extraData) {
            return { previousValue, currentValue };
        }
        const formattedPreviousValue = (extraData.previous_value ||
            []);
        const formattedCurrentValue = (extraData.current_value ||
            []);
        const removedValues = (0, differenceBy_1.default)(formattedPreviousValue, formattedCurrentValue, "id");
        const addedValues = (0, differenceBy_1.default)(formattedCurrentValue, formattedPreviousValue, "id");
        return { previousValue: removedValues, currentValue: addedValues };
    }
    canChangeValue(field, fromValue, toValue) {
        return !(0, fields_1.isFieldRequired)(field) || (toValue === null || toValue === void 0 ? void 0 : toValue.length) !== 0;
    }
    replaceValue(field, record, sourceValue = [], targetValue = [], _userPreferences) {
        return (0, fields_1.replaceValueInList)(field, record, sourceValue, targetValue);
    }
    isValueEmpty(fieldValue, field) {
        if (!(Array.isArray(fieldValue) && fieldValue.length)) {
            return true;
        }
        const records = this.appRecordsService.getMultipleRelatedRecords((0, array_1.shallowUnique)(fieldValue));
        return !records.length;
    }
    showActivityUpdate(fieldValue, field) {
        return (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.length) > 0;
    }
    /**
     * Applies a bulk update to a field value.
     */
    applyBulkUpdate(field, value, update) {
        if (field.params.entries_allowed === "single") {
            return super.applyBulkUpdate(field, value, update);
        }
        return (0, bulk_update_1.applyBulkUpdateToPrimitiveArray)(value, update);
    }
}
exports.BaseReferenceFieldLogic = BaseReferenceFieldLogic;
